import { FC } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_arrow_right } from '@gn-audio/icons';
import {
  Button,
  ButtonVariantType,
  ComponentProps,
  JabraIcon,
  resolveColorProperty,
} from '@gn-audio/jss-components';

import HtmlRender from 'common/components/HtmlRender';

import { usePathMatch } from '../../../../composables/shared/usePathMatch';

import styles from './GlobalDealsRibbon.module.scss';

export interface GlobalDealsRibbonProps extends Omit<ComponentProps, 'rendering'> {
  fields: GlobalDealsRibbon;
}

export type GlobalDealsRibbon = {
  isActive: Field<boolean>;
  message: Field<string>;
  backgroundColor: Field<string>;
  backgroundMessageColor: Field<string>;
  backgroundButtonContainerColor: Field<string>;
  buttonText: Field<string>;
  buttonLink: Field<string>;
  buttonVariant: Field<string>;
  buttonAriaLabel?: Field<string>;
  whitelistPaths: Field<string>;
};

const GlobalDealsRibbon: FC<GlobalDealsRibbonProps> = ({ fields }) => {
  const { isCurrentPathMatched } = usePathMatch();

  const ribbonSettings = fields;
  const isActive = ribbonSettings.isActive.value;
  const pathIsValid = isCurrentPathMatched(ribbonSettings.whitelistPaths.value);

  const bgColor = resolveColorProperty(ribbonSettings.backgroundColor.value);
  const bgMsgColor = resolveColorProperty(ribbonSettings.backgroundMessageColor.value);
  const bgButtonColor = resolveColorProperty(ribbonSettings.backgroundButtonContainerColor.value);

  if (!isActive || !pathIsValid) {
    return null;
  }

  return (
    <div
      style={
        {
          '--deals-ribbon-default-bg': bgColor,
          '--deals-ribbon-text-bg': bgMsgColor,
          '--deals-ribbon-button-bg': bgButtonColor,
        } as React.CSSProperties
      }
      className={classNames(
        'relative mb-0 flex justify-center overflow-hidden bg-cover bg-no-repeat p-0',
        styles.dealsRibbonContainer
      )}
      role="banner"
      aria-label="Deals Ribbon"
    >
      <div className="container">
        <div className="-mx-4 flex flex-wrap">
          <div className="relative w-full max-w-full flex-none align-top">
            <div className="relative flex h-full flex-col items-center justify-between bg-transparent p-2 pb-4 md:flex-row md:p-0">
              <div
                className={classNames(
                  'relative z-20 mb-0 flex h-full flex-grow items-center py-2 pl-6 text-center text-white md:py-5 md:text-left',
                  styles.dealsRibbonText
                )}
              >
                {ribbonSettings.message.value && (
                  <div className="relative z-20 mb-0 text-lg text-white">
                    <HtmlRender
                      element="p"
                      className="my-0 mb-[5px]"
                      html={ribbonSettings.message.value}
                    />
                  </div>
                )}
              </div>
              <div
                className={classNames(
                  'relative z-20 mb-0 flex h-full items-center pl-8 pr-6 text-white',
                  styles.dealsRibbonButton
                )}
              >
                <div>
                  {ribbonSettings.buttonText.value && ribbonSettings.buttonLink.value && (
                    <Button
                      asLink={true}
                      href={ribbonSettings.buttonLink.value}
                      variant={ribbonSettings.buttonVariant.value as ButtonVariantType}
                      size="m"
                      iconPlacement="right"
                      {...(ribbonSettings.buttonAriaLabel?.value && {
                        'aria-label': ribbonSettings.buttonAriaLabel.value,
                      })}
                      icon={<JabraIcon Icon={icon_arrow_right} />}
                    >
                      {ribbonSettings.buttonText.value}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalDealsRibbon;
